import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InstagramIcon from '@mui/icons-material/Instagram';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import IOSSwitch from 'src/components/switch/switch.component';

export default function InstagramAccounts({ accounts = [], isLoading, onConnect, onReauthenticate, onRefresh, refreshingAccount, onDelete, onToggleEnable }) {
  if (!isLoading && !accounts.length) {
    return (
      <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <InstagramIcon sx={{ color: '#E1306C', fontSize: 42, mb: 1 }} />
        <Typography fontWeight={800}>No integrated Instagram accounts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Connect an Instagram business account to manage Instagram messages.
        </Typography>
        <Button variant="contained" onClick={onConnect}>
          Connect Instagram
        </Button>
      </Box>
    );
  }

  if (!accounts.length) return null;

  return (
    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Profile</TableCell>
            <TableCell>Instagram ID</TableCell>
            <TableCell>Facebook Page</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => {
            const isActive = account.is_active !== false;
            const isEnabled = account.is_enable !== false;
            return (
              <TableRow key={account.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar src={account.picture_url || ''} sx={{ bgcolor: alpha('#E1306C', 0.14), color: '#E1306C' }}>
                      <InstagramIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>
                        {account.display_name || 'Instagram Account'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {account.category || 'Instagram profile'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{account.profile_id}</TableCell>
                <TableCell>{account.business_account_id || '-'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip size="small" color={isActive ? 'success' : 'error'} label={isActive ? 'Active' : 'Inactive'} />
                    <Chip size="small" color={isEnabled ? 'primary' : 'default'} label={isEnabled ? 'Enabled' : 'Disabled'} />
                    {Number(account.unread_count || 0) > 0 && <Chip size="small" color="error" label={`${account.unread_count} new`} />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {account.profile_link && (
                      <Tooltip title="Open Instagram profile">
                        <IconButton
                          component="a"
                          href={account.profile_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label="Open Instagram profile"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={isActive ? 'Enable or disable this account' : 'Re-authenticate this account to activate it'}>
                      <span>
                        <IOSSwitch
                          checked={isEnabled && isActive}
                          disabled={!isActive}
                          onChange={(event) => onToggleEnable('instagram', account, event.target.checked)}
                        />
                      </span>
                    </Tooltip>
                    <Button size="small" component={RouterLink} disabled={!isActive || !isEnabled} to={`/instagram-chat?instagram_id=${encodeURIComponent(account.profile_id)}`}>
                      Open Messages
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => onReauthenticate(account)}>
                      Re-authenticate
                    </Button>
                    <Tooltip title="Refresh profile name and picture">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Refresh Instagram profile details"
                          disabled={refreshingAccount === `instagram:${account.id}`}
                          onClick={() => onRefresh('instagram', account)}
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button size="small" color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete('instagram', account)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
